/**
 * Message enrichment service for adding staff information
 * Handles multi-priority lookup and avatar resolution
 * 
 * IMPORTANT: This function stores RAW avatar URLs without proxying.
 * Proxying happens at render time in MessageItemRender to avoid duplicate processing.
 */

import { Message, StaffCacheService } from '../types/conversation';
import { getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';

// Per-message debug output is off: this runs for every message on every conversation open and
// every incoming message, and logging objects for each one is expensive. Flip to console.log to debug.
const debugLog = (..._args: unknown[]): void => {};

/**
 * Apply staff information lookup to a message
 * Priority order:
 * 1. Staff ID lookup (senderStaffId)
 * 2. Contact ID lookup (contactId) - primary Wix identifier
 * 3. Wix User ID lookup (wixUserId)
 * 4. Firestore enriched name (senderStaffName)
 * 5. Staff name lookup by name
 * 6. Embedded extractedStaffInfo
 * 7. Sent message cache
 * 8. Legacy [STAFF:...] format
 */
export const applyStaffLookup = (
  msg: Message,
  staffCache: StaffCacheService
): Message => {
  // PRIORITY 0: If message ALREADY has COMPLETE staff enrichment from Firestore (backend Pub/Sub), USE IT!
  // Backend writes: senderStaffId, senderStaffName, senderStaffAvatar, senderType
  // ONLY skip further enrichment if message has the AVATAR (most critical field)
  // If avatar is missing, continue to staffMessageMeta lookup
  if (msg.senderStaffAvatar) {
    debugLog(`✅ [PRIORITY 0] Using existing complete staff enrichment from Firestore: "${msg.from}"`, {
      senderStaffId: msg.senderStaffId,
      senderStaffName: msg.senderStaffName,
      senderStaffAvatar: msg.senderStaffAvatar ? `✓ ${msg.senderStaffAvatar.substring(0, 60)}...` : '❌ MISSING',
      senderType: msg.senderType,
    });
    return {
      ...msg,
      avatarUrl: msg.senderStaffAvatar, // Mark as having avatar so metadata won't override
    };
  }

  // Check if message has extracted staff info (metadata for workaround messages)
  const embeddedStaffInfo = (
    msg as Message & { extractedStaffInfo?: { staffName: string; avatarUrl?: string } }
  ).extractedStaffInfo;

  // If this is a customer message but has embedded staff info (metadata identifying real staff member),
  // treat it as a staff message and look up from the metadata
  if (msg.isFromCustomer && !embeddedStaffInfo?.staffName) {
    return msg; // Only apply to business messages or messages with staff metadata
  }

  // If message already has staff avatar from Firestore, use it
  if (msg.senderStaffAvatar) {
    return {
      ...msg,
      avatarUrl: msg.senderStaffAvatar, // Store raw URL - proxying happens at render time
    };
  }

  let staffName: string | undefined;
  let staffAvatar: string | undefined;

  // Extract sender info from message
  const senderStaffId = msg.senderStaffId;
  const senderObj = (msg as Message & { sender?: Record<string, unknown> }).sender || {};
  const contactId = senderObj.contactId ? String(senderObj.contactId) : undefined;
  let wixUserId = senderObj.wixUserId ? String(senderObj.wixUserId) : undefined;

  // Also check for wixUserId directly on message (for older messages from pagination)
  if (!wixUserId) {
    const directWixUserId = (msg as Message & { wixUserId?: string }).wixUserId;
    if (directWixUserId) {
      wixUserId = String(directWixUserId);
    }
  }

  // Debug: Log what we're working with
  if (msg.from && msg.from !== 'RS Maids') {
    debugLog(`🔍 [applyStaffLookup] Attempting enrichment for: "${msg.from}"`, {
      senderStaffId,
      contactId: contactId ? 'YES' : 'NO',
      wixUserId: wixUserId ? 'YES' : 'NO',
      senderStaffName: msg.senderStaffName,
      senderType: msg.senderType,
      embeddedStaffInfo: embeddedStaffInfo?.staffName,
      fullSenderObj: senderObj
    });
  }

  // PRIORITY 1: Try to look up by staff ID first
  if (senderStaffId) {
    const staff = staffCache.getStaffById(senderStaffId);
    if (staff) {
      staffName = staff.name;
      staffAvatar = staff.contactAvatar || staff.mainMedia; // Store raw URL
      
      // CRITICAL: Only return if BOTH name AND avatar are found
      if (staffName && staffAvatar) {
        return {
          ...msg,
          from: staffName,
          senderStaffId: senderStaffId,
          senderStaffName: staffName,
          senderStaffAvatar: staffAvatar,
        };
      }
    }
  }

  // PRIORITY 1.5: Check embedded staff info from workaround (highest priority after staff ID)
  // This is for messages sent via the workaround with metadata identifying the real staff member
  if (embeddedStaffInfo?.staffName) {
    debugLog(`🔍 [PRIORITY 1.5] Found embedded staff info: "${embeddedStaffInfo.staffName}"`);
    
    // First try to look up the staff by name to get full cache data
    const staffByName = staffCache.getStaffByName(embeddedStaffInfo.staffName);
    if (staffByName) {
      debugLog(`✅ [PRIORITY 1.5] Found staff in cache: "${staffByName.name}"`);
      staffName = staffByName.name;
      staffAvatar = staffByName.contactAvatar || staffByName.mainMedia || embeddedStaffInfo.avatarUrl; // Store raw URL
      
      // CRITICAL: Only return if BOTH name AND avatar are found
      if (staffName && staffAvatar) {
        return {
          ...msg,
          from: staffName,
          senderStaffName: staffName,
          senderStaffAvatar: staffAvatar,
        };
      }
    }
    
    // If not in cache, use the embedded info directly ONLY if we have both name AND avatar
    if (embeddedStaffInfo.staffName && embeddedStaffInfo.avatarUrl) {
      return {
        ...msg,
        from: embeddedStaffInfo.staffName,
        senderStaffName: embeddedStaffInfo.staffName,
        senderStaffAvatar: embeddedStaffInfo.avatarUrl,
      };
    }
  }

  // PRIORITY 1.8: For messages without staff ID (historical), try to look up by sender name first
  // This handles historical "Helix Tester" messages that don't have senderStaffId populated
  if (!senderStaffId && msg.from && msg.from !== 'RS Maids' && !msg.isFromCustomer) {
    debugLog(`🔍 [PRIORITY 1.8] Historical message without staff ID - looking up by name: "${msg.from}"`);
    const staff = staffCache.getStaffByName(msg.from);
    if (staff) {
      debugLog(`✅ [PRIORITY 1.8] Found staff by name: "${staff.name}"`);
      staffName = staff.name;
      staffAvatar = staff.contactAvatar || staff.mainMedia; // Store raw URL
      
      // CRITICAL: Only return if BOTH name AND avatar are found
      if (staffName && staffAvatar) {
        return {
          ...msg,
          from: staffName,
          senderStaffName: staffName,
          senderStaffAvatar: staffAvatar,
        };
      }
    }
  }

  // CRITICAL CHECK: If parser already extracted staff name from message text (e.g., "Helix: message")
  // Use that name directly without any wixUserId lookup
  // This prevents historical messages from being misidentified
  if (msg.senderStaffName && msg.senderStaffName !== 'RS Maids') {
    debugLog(`✅ [CRITICAL] Using extracted/parsed staff name: "${msg.senderStaffName}" - ATTEMPTING CACHE LOOKUP`);
    
    // First try exact match by full name (e.g., "Helix Tester")
    let staff = staffCache.getStaffByName(msg.senderStaffName);
    
    // If exact match fails, try first-name matching (e.g., "Helix" matches "Helix Tester")
    if (!staff && msg.senderStaffName.length > 0) {
      debugLog(`🔍 [CRITICAL] Exact match failed for "${msg.senderStaffName}", trying first-name matching...`);
      
      // Get all staff and look for first-name match
      const extractedFirstName = msg.senderStaffName.split(' ')[0].toLowerCase();
      if (extractedFirstName) {
        // Try to find any staff whose name STARTS with this first name
        const staffList = staffCache.getAllStaff?.() || [];
        for (const s of staffList) {
          const staffFirstName = s.name?.split(' ')[0]?.toLowerCase();
          if (staffFirstName && staffFirstName === extractedFirstName) {
            debugLog(`✅ [CRITICAL] First-name match found: "${msg.senderStaffName}" → "${s.name}"`);
            staff = s;
            break;
          }
        }
      }
    }
    
    if (staff) {
      debugLog(`✅ [CRITICAL] Found staff in cache: "${staff.name}"`);
      debugLog(`🔍 [CRITICAL] Staff object data:`, {
        name: staff.name,
        contactAvatar: staff.contactAvatar ? `✓ ${staff.contactAvatar.substring(0, 60)}...` : '❌ MISSING',
        mainMedia: staff.mainMedia ? `✓ ${staff.mainMedia.substring(0, 60)}...` : '❌ MISSING',
      });
      staffAvatar = staff.contactAvatar || staff.mainMedia; // Store raw URL
      debugLog(`✅ [CRITICAL] Final staffAvatar selected:`, staffAvatar ? `✓ ${staffAvatar.substring(0, 60)}...` : '❌ NO AVATAR');
      return {
        ...msg,
        from: staff.name,  // Use the full staff name from cache
        senderStaffName: staff.name,
        senderStaffAvatar: staffAvatar,
        // DON'T set avatarUrl here - it will be resolved at render time from senderStaffAvatar
      };
    }
    
    // No cache match, but keep the extracted name - NEVER fall back to wixUserId
    debugLog(`⚠️ [CRITICAL] Staff "${msg.senderStaffName}" not found in cache (exact or first-name), but keeping extracted name - NOT falling back to wixUserId lookup`);
    return {
      ...msg,
      from: msg.senderStaffName,
      senderStaffName: msg.senderStaffName,
    };
  }

  // PRIORITY 2: Try to look up by contact ID (primary way to identify staff from Wix messages)
  if (contactId) {
    debugLog(`🔍 [PRIORITY 2] Looking up staff by contactId: "${contactId}"`);
    const staff = staffCache.getStaffByContactId(contactId);
    if (staff) {
      debugLog(`✅ [PRIORITY 2] Found staff by contactId: "${staff.name}"`);
      staffName = staff.name;
      staffAvatar = staff.contactAvatar || staff.mainMedia; // Store raw URL
      
      // CRITICAL: Only return if BOTH name AND avatar are found
      if (staffName && staffAvatar) {
        return {
          ...msg,
          from: staffName,
          senderStaffName: staffName,
          senderStaffAvatar: staffAvatar,
        };
      }
    } else {
      debugLog(`❌ [PRIORITY 2] Staff NOT found by contactId: "${contactId}". Full senderObj:`, senderObj);
    }
  } else {
    debugLog(`🔍 [PRIORITY 2] No contactId available. senderObj:`, senderObj);
  }

  // PRIORITY 3: Try to look up by wixUserId (BUT SKIP IF IT'S BUSINESS ACCOUNT)
  // Business account wixUserId (efff1c9b-f79b-400c-b76d-e30e572bb2fc) is not a staff member
  const isBusinessAccountWixUserId = wixUserId === 'efff1c9b-f79b-400c-b76d-e30e572bb2fc';
  
  if (wixUserId && !isBusinessAccountWixUserId) {
    debugLog(`🔍 [PRIORITY 3] Looking up staff by wixUserId: "${wixUserId}"`);
    const staff = staffCache.getStaffByWixUserId(wixUserId);
    if (staff) {
      debugLog(`✅ [PRIORITY 3] Found staff by wixUserId: "${staff.name}"`);
      debugLog(`📋 [PRIORITY 3] Staff object data:`, {
        name: staff.name,
        contactAvatar: staff.contactAvatar ? `✓ ${staff.contactAvatar.substring(0, 60)}...` : '❌ MISSING',
        mainMedia: staff.mainMedia ? `✓ ${staff.mainMedia.substring(0, 60)}...` : '❌ MISSING',
      });
      
      staffName = staff.name;
      staffAvatar = staff.contactAvatar || staff.mainMedia; // Store raw URL

      debugLog(`✅ [PRIORITY 3] Final avatar URL:`, staffAvatar ? `✓ ${staffAvatar.substring(0, 80)}...` : '❌ NO AVATAR');
      
      // CRITICAL: Only return if BOTH name AND avatar are found
      if (staffName && staffAvatar) {
        return {
          ...msg,
          from: staffName,
          senderStaffName: staffName,
          senderStaffAvatar: staffAvatar,
        };
      }
    } else {
      debugLog(`❌ [PRIORITY 3] Staff NOT found by wixUserId: "${wixUserId}"`);
    }
  }

  // PRIORITY 4: If message has Firestore enrichment with staff name, use that
  if (msg.senderStaffName && msg.senderType === 'staff') {
    let enhancedAvatar = msg.senderStaffAvatar; // Store raw URL

    // Try to enhance avatar from cache if we have staff ID from Firestore
    if (msg.senderStaffId) {
      const staff = staffCache.getStaffById(msg.senderStaffId);
      if (staff) {
        enhancedAvatar = staff.contactAvatar || staff.mainMedia || enhancedAvatar; // Store raw URL
      }
    }

    return {
      ...msg,
      from: msg.senderStaffName,
      senderStaffAvatar: enhancedAvatar,
    };
  }

    // PRIORITY 5: Try to look up by staff name from Firestore metadata
    if (msg.senderStaffName && !staffName) {
      const staff = staffCache.getStaffByName(msg.senderStaffName);
      if (staff) {
        staffName = staff.name;
        staffAvatar = staff.contactAvatar || staff.mainMedia; // Store raw URL
        
        // CRITICAL: Only return if BOTH name AND avatar are found
        if (staffName && staffAvatar) {
          return {
            ...msg,
            from: staffName,
            senderStaffAvatar: staffAvatar,
          };
        }
      }
    }  // PRIORITY 5.5: For non-customer messages, try to look up by the sender's from name
  // This handles old cached messages that don't have senderStaffId/senderStaffName
  if (!msg.isFromCustomer && msg.from && msg.from !== 'RS Maids' && !staffName) {
    debugLog(`🔍 [PRIORITY 5.5] Attempting name lookup for: "${msg.from}"`);
    const staffByName = staffCache.getStaffByName(msg.from);
    if (staffByName) {
      debugLog(`✅ [PRIORITY 5.5] Found staff by name: "${staffByName.name}"`);
      staffName = staffByName.name;
      staffAvatar = staffByName.contactAvatar || staffByName.mainMedia; // Store raw URL
      
      // CRITICAL: Only return if BOTH name AND avatar are found
      if (staffName && staffAvatar) {
        return {
          ...msg,
          from: staffName,
          senderStaffAvatar: staffAvatar,
        };
      }
    } else {
      debugLog(`❌ [PRIORITY 5.5] Staff NOT found by name: "${msg.from}"`);
    }
  }

  // PRIORITY 6: Parse old [STAFF:...] format from message text
  let oldFormatStaffName: string | undefined;
  let oldFormatAvatarUrl: string | undefined;
  let oldFormatContactId: string | undefined;

  if (!embeddedStaffInfo?.staffName && msg.text) {
    const oldFormatMatch = msg.text.match(/^\[STAFF:([^|]+)\|([^|]+)\|([^|]+)\|?([^\]]*)\]/);
    if (oldFormatMatch) {
      oldFormatStaffName = oldFormatMatch[1];
      oldFormatContactId = oldFormatMatch[3];
      oldFormatAvatarUrl = oldFormatMatch[4] || undefined;

      debugLog(
        `🔄 Parsed old [STAFF:...] format: staffName="${oldFormatStaffName}", contactId="${oldFormatContactId}", avatarUrl="${oldFormatAvatarUrl}"`
      );
    }
  }

  debugLog(
    `🔍 applyStaffLookup: msg.from="${msg.from}", embeddedStaffInfo="${embeddedStaffInfo?.staffName}", oldFormat="${oldFormatStaffName}"`
  );

  // PRIORITY 7: Use embedded staff info from message text (if no staff ID match)
  if (embeddedStaffInfo?.staffName) {
    staffName = embeddedStaffInfo.staffName;
    staffAvatar = embeddedStaffInfo.avatarUrl;
    debugLog(`✅ Using embedded staff name: "${staffName}", avatar: "${staffAvatar}"`);
  }
  // PRIORITY 8: Use old [STAFF:...] format if no embedded info
  else if (oldFormatStaffName) {
    staffName = oldFormatStaffName;
    staffAvatar = oldFormatAvatarUrl;
    debugLog(`✅ Using old format staff name: "${staffName}", avatar: "${staffAvatar}"`);

    // Try to enhance avatar from cache if available
    if (oldFormatContactId) {
      const staff = staffCache.getStaffByContactId(oldFormatContactId);
      if (staff?.mainMedia) {
        staffAvatar = staff.mainMedia;
        debugLog(`✅ Enhanced avatar from cache: ${staffAvatar}`);
      }
    }
  }

  // If we found staff info, update the message
  if (staffName || staffAvatar) {
    const newAvatarUrl = staffAvatar || msg.avatarUrl;
    const finalName = staffName || msg.from;

    // Clean up old [STAFF:...] format from message text if present
    let cleanedText = msg.text;
    if (oldFormatStaffName && msg.text.startsWith('[STAFF:')) {
      cleanedText = msg.text.replace(/^\[STAFF:[^\]]+\]/, '');
      debugLog(`🧹 Cleaned old format from message text: "${cleanedText}"`);
    }

    debugLog(
      `✅ applyStaffLookup UPDATED: from="${finalName}", avatarUrl="${newAvatarUrl}"`
    );
    return {
      ...msg,
      text: cleanedText,
      from: finalName,
      avatarUrl: newAvatarUrl,
    };
  }

  // For messages that can't be looked up, set appropriate fallback avatar
  if (!msg.avatarUrl) {
    // Business messages: only provide fallback avatar for messages that already have staff identification
    if (!msg.isFromCustomer) {
      // For staff names that aren't RS Maids, generate default avatar with initials
      if (msg.from !== 'RS Maids') {
        // For other business messages, generate default avatar with initials
        debugLog(`✅ Generating default avatar for "${msg.from}"`);
        return {
          ...msg,
          avatarUrl: getDefaultAvatar(msg.from)
        };
      } else {
        // For RS Maids messages WITHOUT staff fields, set company logo as fallback
        // Metadata lookup will attempt to find the real staff member first
        // If metadata lookup finds data, it will override this avatarUrl
        const companyLogoUrl = '/assets/icon/maskable_icon_192.png';
        debugLog(`⏸️ Setting company logo for RS Maids - metadata lookup will override if available`);
        return {
          ...msg,
          avatarUrl: companyLogoUrl
        };
      }
    }
  }
  
  // Return message as-is
  return msg;
};

/**
 * Batch apply staff lookup to multiple messages
 */
export const batchApplyStaffLookup = (
  messages: Message[],
  staffCache: StaffCacheService
): Message[] => {
  return messages.map((msg) => applyStaffLookup(msg, staffCache));
};
